import { DatePicker, Form, FormItem, Input, NavigationModal } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { NAMESPACE } from 'locale'
import { transferRecordConstants as trConstants } from 'locale/transferRecords'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type {
  TransferRecordPayload,
  TransferRecordType
} from 'types/transferRecord'
import RotaWarningBox from './components/RotaWarningBox'

/** Stored on the transfer record; list UI uses translated label for display. */
const DISCHARGE_RECORD_REASON = 'Discharge'

type TransferDetailsModalProps = {
  showModal: boolean
  onCancel: () => void
  onConfirm: (data: TransferRecordPayload) => void
  backNavigation: () => void
  name: string
  type: TransferRecordType
  initialValues?: TransferDetailsFormValues
  originalReason?: string
  linkedTransferRecord?: string
  isLoading?: boolean
  showRotaWarning?: boolean
}

type TransferDetailsFormValues = {
  date?: Dayjs
  reason?: string
  transferredFromLocationName?: string
  transferredFromAddress?: string
  transferredFromCareSettingType?: string
  transferredToLocationName?: string
  transferredToAddress?: string
  transferredToCareSettingType?: string
}

const TransferDetailsModal = ({
  showModal,
  onCancel,
  onConfirm,
  backNavigation,
  name,
  type,
  initialValues,
  originalReason,
  linkedTransferRecord,
  isLoading,
  showRotaWarning = false
}: TransferDetailsModalProps) => {
  const { t } = useTranslation(NAMESPACE.TRANSFER_RECORDS)
  const [form] = AntdForm.useForm<TransferDetailsFormValues>()

  const isHospitalisation = type === 'hospitalisation'
  const isReturn = type === 'return'
  const isDischarge = type === 'discharge'

  const getTitle = () => {
    if (isDischarge) return t(trConstants.WAS_DISCHARGED, { name })
    if (isReturn) return t(trConstants.HAS_RETURNED, { name })
    if (isHospitalisation) return t(trConstants.WAS_HOSPITALISED, { name })
    return t(trConstants.WAS_TRANSFERRED, { name })
  }

  const getDateLabel = () => {
    if (isDischarge) return t(trConstants.DATE_OF_DISCHARGE)
    if (isReturn) return t(trConstants.DATE_OF_RETURN)
    if (isHospitalisation) return t(trConstants.DATE_OF_HOSPITALISATION)
    return t(trConstants.DATE_OF_TRANSFER)
  }

  const getReasonLabel = () => {
    if (isHospitalisation) return t(trConstants.REASON_FOR_HOSPITALISATION)
    return t(trConstants.REASON_FOR_TRANSFER)
  }

  const getToPlaceholders = () => {
    if (isHospitalisation) {
      return {
        name: t(trConstants.HOSPITAL_NAME),
        address: t(trConstants.HOSPITAL_ADDRESS)
      }
    }
    return {
      name: t(trConstants.LOCATION_NAME),
      address: t(trConstants.ADDRESS)
    }
  }

  const getRotaStatus = () => {
    if (isDischarge) return 'discharged'
    if (isHospitalisation) return 'hospitalised'
    return 'transferred'
  }

  const handleConfirm = async () => {
    let values: TransferDetailsFormValues
    try {
      values = await form.validateFields()
    } catch {
      // validation errors are rendered against the fields themselves
      return
    }

    if (isDischarge) {
      const date = values.date
      if (!date) return
      const payload: TransferRecordPayload = {
        type: 'discharge',
        date: date.second(0).millisecond(0).toISOString(),
        reason: DISCHARGE_RECORD_REASON,
        transferredFrom: {
          locationName: values.transferredFromLocationName || ''
        }
      }
      onConfirm(payload)
      return
    }

    const payload: TransferRecordPayload = {
      type,
      date: values.date?.format('YYYY-MM-DD') || null,
      reason: values.reason || '',
      transferredFrom: {
        locationName: values.transferredFromLocationName || '',
        address: values.transferredFromAddress || undefined,
        careSettingType: values.transferredFromCareSettingType || undefined
      },
      transferredTo: {
        locationName: values.transferredToLocationName || '',
        address: values.transferredToAddress || undefined,
        careSettingType: values.transferredToCareSettingType || undefined
      },
      ...(linkedTransferRecord && { linkedTransferRecord })
    }
    onConfirm(payload)
  }

  const getInitialValues = (): TransferDetailsFormValues => {
    if (!initialValues) return {}
    return {
      date: initialValues.date ? dayjs(initialValues.date) : undefined,
      reason: initialValues.reason || undefined,
      transferredFromLocationName:
        initialValues.transferredFromLocationName || undefined,
      transferredFromAddress: initialValues.transferredFromAddress || undefined,
      transferredFromCareSettingType:
        initialValues.transferredFromCareSettingType || undefined,
      transferredToLocationName:
        initialValues.transferredToLocationName || undefined,
      transferredToAddress: initialValues.transferredToAddress || undefined,
      transferredToCareSettingType:
        initialValues.transferredToCareSettingType || undefined
    }
  }

  const toPlaceholders = getToPlaceholders()

  return (
    <NavigationModal
      modalTitle={getTitle()}
      modalConfirmText={t(trConstants.SAVE)}
      modalCancelText={t(trConstants.CANCEL)}
      showPrompt={showModal}
      confirmNavigation={handleConfirm}
      cancelNavigation={onCancel}
      confirmButtonProps={{ variant: 'primary', isLoading }}
      hasBackButton
      backButtonText={t(trConstants.BACK)}
      backNavigation={backNavigation}
      width={640}
    >
      <Container>
        {isReturn && originalReason && (
          <OriginalReasonText data-testid="original-transfer-reason">
            {t(trConstants.ORIGINAL_TRANSFER_REASON, {
              reason: originalReason
            })}
          </OriginalReasonText>
        )}
        <Form form={form} layout="vertical" initialValues={getInitialValues()}>
          {isDischarge ? (
            <>
              <FormItem
                label={getDateLabel()}
                name="date"
                rules={[
                  {
                    required: true,
                    message: t(trConstants.DATE_REQUIRED)
                  }
                ]}
              >
                <StyledDatePicker
                  format="DD-MM-YYYY HH:mm"
                  showTime={{ format: 'HH:mm' }}
                  data-testid="transfer-details-date-picker"
                />
              </FormItem>
              <FormItem
                label={t(trConstants.DISCHARGED_FROM)}
                name="transferredFromLocationName"
                rules={[
                  {
                    required: true,
                    message: t(trConstants.DISCHARGED_FROM_REQUIRED)
                  }
                ]}
              >
                <Input data-testid="transfer-details-discharge-from" />
              </FormItem>
            </>
          ) : (
            <>
              <FormItem label={getDateLabel()} name="date">
                <StyledDatePicker
                  format="DD-MM-YYYY"
                  data-testid="transfer-details-date-picker"
                />
              </FormItem>

              <FormItem
                label={getReasonLabel()}
                name="reason"
                rules={[
                  { required: true, message: t(trConstants.REASON_REQUIRED) }
                ]}
              >
                <Input data-testid="transfer-details-reason" />
              </FormItem>

              <FieldGroupLabel>
                <Text weight="medium">{t(trConstants.TRANSFERRED_FROM)}</Text>
                <FormItem
                  label={t(trConstants.LOCATION_NAME)}
                  name="transferredFromLocationName"
                  rules={[
                    {
                      required: true,
                      message: t(trConstants.LOCATION_NAME_REQUIRED)
                    }
                  ]}
                >
                  <Input data-testid="transfer-details-from-location" />
                </FormItem>
                <FormItem
                  label={t(trConstants.ADDRESS)}
                  name="transferredFromAddress"
                >
                  <Input data-testid="transfer-details-from-address" />
                </FormItem>
                <FormItem
                  label={t(trConstants.CARE_SETTING_TYPE)}
                  name="transferredFromCareSettingType"
                >
                  <Input data-testid="transfer-details-from-care-setting" />
                </FormItem>
              </FieldGroupLabel>

              <FieldGroupLabel>
                <Text weight="medium">{t(trConstants.TRANSFERRED_TO)}</Text>
                <FormItem
                  label={toPlaceholders.name}
                  name="transferredToLocationName"
                >
                  <Input data-testid="transfer-details-to-location" />
                </FormItem>
                <FormItem
                  label={toPlaceholders.address}
                  name="transferredToAddress"
                >
                  <Input data-testid="transfer-details-to-address" />
                </FormItem>
                <FormItem
                  label={t(trConstants.CARE_SETTING_TYPE)}
                  name="transferredToCareSettingType"
                >
                  <Input data-testid="transfer-details-to-care-setting" />
                </FormItem>
              </FieldGroupLabel>
            </>
          )}
        </Form>
        {showRotaWarning && (
          <RotaWarningBox
            name={name}
            status={getRotaStatus()}
            confirmButtonText={t(trConstants.SAVE)}
          />
        )}
      </Container>
    </NavigationModal>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 300px;
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`

const FieldGroupLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const OriginalReasonText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
  font-style: italic;
  margin-bottom: 8px;
`

export default TransferDetailsModal
